import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../../api/client';
import { useToast } from '../../../hooks/useToast';
import { FiCheck, FiCreditCard, FiSend } from 'react-icons/fi';

const Billing = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankTransferInstructions, setBankTransferInstructions] = useState(null);

  const [gatewayForm, setGatewayForm] = useState({ paymentProviderCode: 'bank_transfer', isEnabled: true, bankAccountId: '', paymentInstructions: '' });

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/subscriptions/current').catch(() => ({ data: { data: null } })),
      apiClient.get('/subscriptions/plans'),
      apiClient.get('/subscriptions/current/payments').catch(() => ({ data: { data: [] } })),
      apiClient.get('/subscriptions/current/payment-gateways').catch(() => ({ data: { data: [] } })),
      apiClient.get('/banking/accounts').catch(() => ({ data: { data: [] } })),
      apiClient.get('/subscriptions/payment-providers').catch(() => ({ data: { data: [] } })),
    ]).then(([subRes, planRes, payRes, gwRes, bankRes, provRes]) => {
      setSubscription(subRes.data.data);
      setPlans(planRes.data.data);
      setPayments(payRes.data.data);
      setGateways(gwRes.data.data);
      setBankAccounts(bankRes.data.data);
      setProviders(provRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load billing data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const planName = (id) => plans.find((p) => p.id === id)?.name || id;

  const providerLabel = (paymentProviderId) => {
    const provider = providers.find((pr) => pr.id === paymentProviderId);
    return provider ? (provider.name || provider.code?.replace('_', ' ')) : '—';
  };

  const handleSubscribe = async (planCode) => {
    try {
      await apiClient.post('/subscriptions/current', { planCode });
      success('✅ Subscribed');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not subscribe')); }
  };

  const handleChangePlan = async (planCode) => {
    if (!window.confirm(`Switch to this plan now?`)) return;
    try {
      await apiClient.post('/subscriptions/current/change-plan', { planCode });
      success('✅ Plan changed');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not change plan')); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You can reactivate later.')) return;
    try {
      await apiClient.post('/subscriptions/current/cancel', {});
      success('✅ Subscription cancelled');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not cancel subscription')); }
  };

  const handleReactivate = async () => {
    try {
      await apiClient.post('/subscriptions/current/reactivate');
      success('✅ Subscription reactivated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not reactivate subscription')); }
  };

  const handlePayViaBankTransfer = async () => {
    try {
      const { data } = await apiClient.post('/subscriptions/current/payments/bank-transfer');
      setBankTransferInstructions(data.data.instructions);
      success('✅ Bank transfer payment created — see instructions below');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create bank transfer payment')); }
  };

  const handleUpsertGateway = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...gatewayForm };
      if (!payload.bankAccountId) delete payload.bankAccountId;
      if (!payload.paymentInstructions) delete payload.paymentInstructions;
      await apiClient.post('/subscriptions/current/payment-gateways', payload);
      success('✅ Payment gateway saved');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not save gateway')); }
  };

  if (loading) {
    return (
      <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <p style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</p>
      </motion.div>
    );
  }

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Billing</h1>
        <p>Your subscription, payment history, and the payment methods you accept from customers</p>
      </div>

      <section className="page-section">
        <div className="section-header"><h2>Current Subscription</h2></div>
        {subscription ? (
          <>
            <div className="stats-row" style={{ marginBottom: '1rem' }}>
              <div className="stat-box"><div className="label">Plan</div><div className="value">{planName(subscription.subscriptionPlanId)}</div></div>
              <div className="stat-box"><div className="label">Status</div><div className="value" style={{ textTransform: 'capitalize' }}>{subscription.status}</div></div>
              <div className="stat-box"><div className="label">Current Period Ends</div><div className="value">{subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : '—'}</div></div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {subscription.status === 'active' && (
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handleCancel}>Cancel Subscription</button>
              )}
              {subscription.status === 'cancelled' && (
                <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={handleReactivate}>Reactivate</button>
              )}
              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handlePayViaBankTransfer}>
                <FiSend size={14} /> Pay via Bank Transfer
              </button>
            </div>
            {bankTransferInstructions && (
              <div style={{ marginTop: '1rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '1rem', fontSize: '0.85rem', color: '#1e40af' }}>
                {bankTransferInstructions}
              </div>
            )}
          </>
        ) : (
          <p style={{ color: '#6b7280' }}>No active subscription — choose a plan below.</p>
        )}
      </section>

      <section className="page-section">
        <div className="section-header"><h2>Plans</h2></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {plans.map((p) => {
            const isCurrent = subscription?.subscriptionPlanId === p.id;
            return (
              <div key={p.id} style={{ border: isCurrent ? '2px solid #3b82f6' : '1px solid #e5e7eb', borderRadius: '8px', padding: '1.25rem', background: 'white' }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>{p.name}</h3>
                <p style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0.5rem 0' }}>£{Number(p.price).toFixed(2)}<span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#6b7280' }}>/{p.billingInterval === 'monthly' ? 'mo' : 'yr'}</span></p>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', minHeight: '2.5em' }}>{p.description}</p>
                {isCurrent ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#3b82f6', fontWeight: 600, fontSize: '0.85rem' }}>
                    <FiCheck size={16} /> Current Plan
                  </div>
                ) : (
                  <button
                    className="btn btn-sm btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => subscription ? handleChangePlan(p.code) : handleSubscribe(p.code)}
                  >
                    {subscription ? 'Switch to This Plan' : 'Subscribe'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="page-section">
        <div className="section-header"><h2>Payment History</h2></div>
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Date</th><th>Amount</th><th>Status</th><th>Reference</th></tr></thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No payments yet</td></tr>
              ) : payments.map((p) => (
                <tr key={p.id}>
                  <td>{new Date(p.attemptedAt).toLocaleDateString()}</td>
                  <td style={{ fontWeight: 600 }}>£{Number(p.amount).toFixed(2)}</td>
                  <td><span className="badge" style={{ background: p.status === 'succeeded' ? '#10b981' : p.status === 'failed' ? '#ef4444' : '#f59e0b', color: 'white' }}>{p.status}</span></td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.providerTransactionRef || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="page-section">
        <div className="section-header"><h2><FiCreditCard size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />Payment Gateways You Accept</h2></div>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Which methods your own customers can pay you through — separate from how you pay your own subscription above.
        </p>
        <form onSubmit={handleUpsertGateway} className="form-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="form-field">
            <label>Provider *</label>
            <select value={gatewayForm.paymentProviderCode} onChange={(e) => setGatewayForm(p => ({ ...p, paymentProviderCode: e.target.value }))}>
              <option value="">-- select --</option>
              {providers.map((p) => <option key={p.id} value={p.code}>{p.name || p.code.replace('_', ' ')}</option>)}
            </select>
          </div>
          {gatewayForm.paymentProviderCode === 'bank_transfer' && (
            <div className="form-field">
              <label>Bank Account</label>
              <select value={gatewayForm.bankAccountId} onChange={(e) => setGatewayForm(p => ({ ...p, bankAccountId: e.target.value }))}>
                <option value="">-- select --</option>
                {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.accountName}</option>)}
              </select>
            </div>
          )}
          <div className="form-field">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
              <input type="checkbox" checked={gatewayForm.isEnabled} onChange={(e) => setGatewayForm(p => ({ ...p, isEnabled: e.target.checked }))} style={{ width: 'auto' }} />
              Enabled
            </label>
          </div>
          <div className="form-field full-width"><label>Payment Instructions</label><input value={gatewayForm.paymentInstructions} onChange={(e) => setGatewayForm(p => ({ ...p, paymentInstructions: e.target.value }))} placeholder="Quote your invoice number as reference" /></div>
          <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save</button></div>
        </form>
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Provider</th><th>Bank Account</th><th>Enabled</th><th>Instructions</th></tr></thead>
            <tbody>
              {gateways.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No gateways configured yet</td></tr>
              ) : gateways.map((g) => (
                <tr key={g.id}>
                  <td style={{ textTransform: 'capitalize' }}>{providerLabel(g.paymentProviderId)}</td>
                  <td>{bankAccounts.find((a) => a.id === g.bankAccountId)?.accountName || '—'}</td>
                  <td><span className="badge" style={{ background: g.isEnabled ? '#10b981' : '#9ca3af', color: 'white' }}>{g.isEnabled ? 'Yes' : 'No'}</span></td>
                  <td style={{ fontSize: '0.85rem', color: '#6b7280' }}>{g.paymentInstructions || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </motion.div>
  );
};

export default Billing;